import {createStore} from 'vuex';
import {actions, mutations} from '@/assets/js/vue/vuex/actions';
import {endpoints, traccarEndpoints} from '@/assets/js/vue/vuex/endpoints';
import requestHelper from '@/assets/js/vue/vuex/request-helper';
import {dataAggregator, dataManipulator, entityDataManager} from '@/assets/js/data-manipulation';
import notification from "@/assets/js/notification";
import i18n from '@/assets/js/vue/i18n';
import {EventSeverity} from "@/assets/js/events-info";
import { saveAs } from 'file-saver';
import locale from "@/assets/js/locale";
import moment from "moment/moment";
import {MaintenanceStatus} from "@/assets/js/enums/MaintenanceStatus.ts";

const getDefaultTasksFilterDateFrom = () => moment().startOf('day').subtract(2, 'day').toDate();
const getDefaultTasksFilterDateTo = () => moment().endOf('day').add(7, 'day').toDate();

// Create a new store instance.
const store = createStore({
  state() {
    return {
      isLoading: false,
      loadingMessage: null,
      lastErrorDetails: null,
      isGuestMode: false,
      newView: window.localStorage.getItem(mutations.SET_USE_NEW_DASHBOARD_VIEW) === 'true',
      session: {},
      users: [],
      companies: [],
      groups: [],
      positions: [],
      devices: [],
      drivers: [],
      geofences: [],
      userGeoFences: [],
      deviceDrivers: new Map(),
      deviceIgnitions: new Map(),

      makes: [],
      models: [],
      vehicles: [],
      vehicleRecipients: [],

      violations: null,
      maintenances: [],
      traccarMaintenances: [],

      // Trips
      tripsFilterDateFrom: null,
      tripsFilterDateTo: null,
      tripsFilterDevice: null,
      tripsFilterDriver: null,
      tripsFilterGroup: null,
      tripsFilterIncludeAddresses: false,
      garageFilterDevice: [],
      garageFilterStatus: [],
      tripsData: [],
      tripsRecordPositions: [],
      tripsMapData: null,

      // Events
      events: [],
      eventsPanelOpened: false,
      eventsFilterDateFrom: null,
      eventsFilterDateTo: null,
      eventsFilterDeviceIds: [],
      eventsFilterStatuses: [],
      eventsRecords: [],
      userAssignedEvents: [],

      //Tasks
      tasks: [],
      tasksFilterStatus: null,
      tasksFilterDateFrom: getDefaultTasksFilterDateFrom(),
      tasksFilterDateTo: getDefaultTasksFilterDateTo(),
      tasksFilterDriver: null,
      tasksFilterVehicle: null,
      tasksFilterTags: [],
      taskSchedules: [],
    };
  },
  getters: {
    isLogged(state) {
      return state.session?.id !== undefined;
    },
    userId(state) {
      return state.session?.id;
    },
    userEmail(state) {
      return state.session?.email;
    },
    isAdmin(state) {
      return state.session.administrator !== undefined && state.session.administrator === true;
    },
    isAdminUser(state) {
      return state.session?.id === +process.env.VUE_APP_ADMIN_ID;
    },
    isOazisCompany(state, getters) {
      return getters.userCompany?.id === +process.env.VUE_APP_OAZIS_COMPANY_ID;
    },
    makesAlphabetical(state) {
      return state.makes.sort(dataManipulator.sortByKey('name'));
    },
    modelsAlphabetical(state) {
      return state.models.sort(dataManipulator.sortByKey('name'));
    },
    hasViolations(state) {
      return state.violations?.length > 0;
    },
    userCompany(state) {
      return entityDataManager.getCompanyByUserId(state.companies, state.session.id);
    },
    dashboardDeviceColumns(state) {
      const company = entityDataManager.getCompanyByUserId(state.companies, state.session.id);
      return company?.attributes?.dashboardDeviceColumns;
    },
    dashboardQuickMapInfoFields(state) {
      const company = entityDataManager.getCompanyByUserId(state.companies, state.session.id);
      return company?.attributes?.dashboardQuickMapInfoFields;
    },
    showZonesInDashboard(state, getters) {
      return getters.userCompany?.attributes?.showZonesInDashboard ?? false;
    },
    allGeoFences(state) {
      return [...state.geofences, ...state.userGeoFences];
    },
    isGroupShown() {
      return process.env.VUE_APP_INCLUDE_GROUP_NAME_IN_DASHBOARD === "1";
    },
    googleMapsApiKey() {
      return process.env.VUE_APP_GOOGLE_API_KEY;
    },
    googleRoutesApiKey() {
      return process.env.VUE_APP_GOOGLE_ROUTES_API_KEY;
    }
  },
  mutations: {
    [mutations.SET_IS_LOADING](state, payload) {
      const isLoading = payload.isLoading || false;
      state.isLoading = isLoading;
      state.loadingMessage = payload.message ?? null;

      if (isLoading) {
        state.lastErrorDetails = null;
      } else if (payload.errors !== undefined) {
        state.lastErrorDetails = payload.errors;
      }
    },
    [mutations.SET_SESSION](state, payload) {
      state.session = payload;
    },
    [mutations.SET_USERS](state, payload) {
      state.users = payload;
    },
    [mutations.SET_COMPANIES](state, payload) {
      state.companies = payload;
    },
    [mutations.SET_GROUPS](state, payload) {
      state.groups = payload;
    },
    [mutations.APPEND_POSITIONS](state, payload) {
      state.positions = dataManipulator.replaceAllByKey(state.positions, payload, 'deviceId');
    },
    [mutations.CLEAR_POSITIONS](state) {
      state.positions = [];
    },
    [mutations.SET_DEVICES](state, payload) {
      state.devices = payload;
    },
    [mutations.APPEND_DEVICES](state, payload) {
      state.devices = dataManipulator.replaceAllByKey(state.devices, payload);
    },

    [mutations.SET_MAKES](state, payload) {
      state.makes = payload;
    },
    [mutations.SET_MODELS](state, payload) {
      state.models = payload;
    },
    [mutations.SET_VEHICLES](state, payload) {
      state.vehicles = payload;
    },
    [mutations.SET_VEHICLE](state, payload) {
      const index = state.vehicles.findIndex(x => x.id === payload.id);
      if (index !== -1) {
        state.vehicles[index] = payload;
      }
    },

    [mutations.SET_VEHICLE_RECIPIENTS](state, payload) {
      state.vehicleRecipients = payload;
    },

    [mutations.SET_VIOLATIONS](state, payload) {
      state.violations = payload
    },
    [mutations.SET_MAINTENANCES](state, payload) {
      state.maintenances = payload;
    },
    [mutations.SET_TRACCAR_MAINTENANCES](state, payload) {
      state.traccarMaintenances = payload;
    },

    [mutations.SET_TRIPS_FILTER_DATE_FROM](state, payload) {
      state.tripsFilterDateFrom = payload?.date || null;
    },
    [mutations.SET_TRIPS_FILTER_DATE_TO](state, payload) {
      state.tripsFilterDateTo = payload?.date || null;
    },
    [mutations.SET_TRIPS_FILTER_DEVICE_ID](state, payload) {
      state.tripsFilterDevice = payload || null;
    },
    [mutations.SET_TRIPS_FILTER_DRIVER](state, payload) {
      state.tripsFilterDriver = payload || null;
    },
    [mutations.SET_TRIPS_FILTER_GROUP](state, payload) {
      state.tripsFilterGroup = payload || null
    },
    [mutations.SET_TRIPS_FILTER_INCLUDE_ADDRESSES](state, payload) {
      state.tripsFilterIncludeAddresses = payload === true;
    },
    [mutations.SET_GARAGE_FILTER_DEVICE](state, payload) {
      state.garageFilterDevice = payload || [];
    },
    [mutations.SET_GARAGE_FILTER_STATUS](state, payload) {
      state.garageFilterStatus = payload || [];
    },
    [mutations.SET_TRIPS_DATA](state, payload) {
      state.tripsData = payload;
    },
    [mutations.SET_TRIPS_RECORD_POSITIONS](state, payload) {
      state.tripsRecordPositions = payload;
    },
    [mutations.SET_TRIPS_MAP_DATA](state, payload) {
      state.tripsMapData = payload;
    },
    [mutations.CLEAR_TRIPS_MAP_DATA](state) {
      state.tripsMapData = null;
    },
    [mutations.SET_DRIVERS](state, payload) {
      state.drivers = payload;
    },
    [mutations.SET_GEO_FENCES](state, payload) {
      state.geofences = payload;
    },
    [mutations.SET_USER_GEO_FENCES](state, payload) {
      state.userGeoFences = payload;
    },
    [mutations.SET_DEVICE_DRIVER](state, payload) {
      if (!payload.beaconUUID) {
        state.deviceDrivers.delete(payload.deviceId);
      }
      else {
        state.deviceDrivers.set(payload.deviceId, payload);
      }
    },
    [mutations.CLEAR_DEVICE_DRIVERS](state) {
      state.deviceDrivers.clear();
    },

    [mutations.TOGGLE_EVENTS_PANEL](state, payload) {
      state.eventsPanelOpened = payload;
      if (state.eventsPanelOpened) {
        state.events.forEach(x => {
          x.isSeen = true;
          clearTimeout(x.alarmTimeout);
        });
      }
    },
    [mutations.APPEND_EVENT](state, payload) {
      state.events.unshift(payload);
    },
    [mutations.CLEAR_EVENTS](state) {
      state.events = [];
    },
    [mutations.SET_EVENTS_FILTER_DATE_FROM](state, payload) {
      state.eventsFilterDateFrom = payload?.date || null;
    },
    [mutations.SET_EVENTS_FILTER_DATE_TO](state, payload) {
      state.eventsFilterDateTo = payload?.date || null;
    },
    [mutations.SET_EVENTS_FILTER_DEVICE_ID](state, payload) {
      state.eventsFilterDeviceIds = payload || [];
    },
    [mutations.SET_EVENTS_FILTER_STATUSES](state, payload) {
      state.eventsFilterStatuses = payload || [];
    },
    [mutations.SET_EVENTS_RECORDS](state, payload) {
      state.eventsRecords = payload;
    },
    [mutations.SET_CURRENT_USER_ASSIGNED_EVENTS](state, payload) {
      state.userAssignedEvents = payload;
    },
    [mutations.SET_DEVICE_IGNITION](state, payload) {
      state.deviceIgnitions.set(payload.deviceId, {ignition: payload.ignition, timestamp: payload.timestamp});
    },
    [mutations.CLEAR_DEVICE_IGNITIONS](state) {
      state.deviceIgnitions.clear();
    },
    [mutations.SET_TASKS](state, payload) {
      state.tasks = payload;
    },
    [mutations.SET_TASK](state, payload) {
      const tasks = [...state.tasks];
      const index = tasks.findIndex(x => x.id === payload.id);
      if (index >= 0) {
        tasks[index] = payload;
      }
      else {
        tasks.push(payload);
      }

      state.tasks = tasks;
    },
    [mutations.REMOVE_TASK](state, payload) {
      state.tasks = state.tasks.filter(x => x.id !== payload.id);
    },
    [mutations.SET_TASKS_FILTER_STATUS](state, payload) {
      state.tasksFilterStatus = payload ?? null;
    },
    [mutations.SET_TASKS_FILTER_DATE_FROM](state, payload) {
      state.tasksFilterDateFrom = payload ? moment(payload).startOf('day').toDate() : null;
    },
    [mutations.SET_TASKS_FILTER_DATE_TO](state, payload) {
      state.tasksFilterDateTo = payload ? moment(payload).endOf('day').toDate() : null;
    },
    [mutations.SET_TASKS_FILTER_DRIVER](state, payload) {
      state.tasksFilterDriver = payload || null;
    },
    [mutations.SET_TASKS_FILTER_VEHICLE](state, payload) {
      state.tasksFilterVehicle = payload || null;
    },
    [mutations.SET_TASKS_FILTER_TAGS](state, payload) {
      state.tasksFilterTags = payload || [];
    },
    [mutations.RESET_TASKS_FILTERS](state) {
      state.tasksFilterStatus = null;
      state.tasksFilterDateFrom = getDefaultTasksFilterDateFrom();
      state.tasksFilterDateTo = getDefaultTasksFilterDateTo();
      state.tasksFilterDriver = null;
      state.tasksFilterVehicle = null;
      state.tasksFilterTags = [];
    },
    [mutations.SET_TASK_SCHEDULES](state, payload) {
      state.taskSchedules = payload;
    },
    [mutations.SET_TASK_SCHEDULE](state, payload) {
      const taskSchedules = [...state.taskSchedules];
      const index = taskSchedules.findIndex(x => x.id === payload.id);
      if (index >= 0) {
        taskSchedules[index] = payload;
      }
      else {
        taskSchedules.push(payload);
      }

      state.taskSchedules = taskSchedules;
    },
    [mutations.REMOVE_TASK_SCHEDULE](state, payload) {
      state.taskSchedules = state.taskSchedules.filter(x => x.id !== payload.id);
    },
    [mutations.SET_GUEST_MODE](state, payload) {
      state.isGuestMode = payload;
    },
    [mutations.SET_LOCALE](_, newLocale) {
      locale.setLocale(newLocale);
      i18n.global.locale = newLocale;
      document.title = i18n.global.t('title');
      document.querySelector('head').lang = newLocale;
    },
    [mutations.SET_USE_NEW_DASHBOARD_VIEW](state, payload) {
      state.newView = payload;
      localStorage.setItem(mutations.SET_USE_NEW_DASHBOARD_VIEW, payload);
    },
    [mutations.SET_USE_DEFAULT_DASHBOARD_VIEW](state) {
      state.newView = false;
      localStorage.setItem(mutations.SET_USE_NEW_DASHBOARD_VIEW, false.toString());
    },
  },
  actions: {
    async [actions.GET_SESSION]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.SESSIONS;
      const response = await requestHelper.apiGet(endpoint);

      const json = response.ok ? (await response.json() || {}) : {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_SESSION, json);

      return json;
    },
    async [actions.CREATE_SESSION]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});
      commit(mutations.SET_USE_DEFAULT_DASHBOARD_VIEW);
      commit(mutations.CLEAR_TRIPS_MAP_DATA);

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.SESSIONS;
      const response = await requestHelper.apiPostAsForm(endpoint, payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-create.session');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_SESSION, json);

      const {email, password} = payload;
      const credentials = Buffer.from(`${email}:${password}`).toString('base64');

      window.localStorage.setItem('_token', credentials);

      return json;
    },
    async [actions.CLOSE_SESSION]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});
      commit(mutations.CLEAR_TRIPS_MAP_DATA);

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.SESSIONS;
      const response = await requestHelper.apiDelete(endpoint);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.session');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_SESSION, {});
      commit(mutations.SET_USE_DEFAULT_DASHBOARD_VIEW);

      window.localStorage.removeItem('_token');
    },

    async [actions.GET_USERS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.USERS;
      const response = await requestHelper.apiGet(endpoint);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.users');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_USERS, json);

      return json;
    },
    async [actions.GET_DRIVERS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + traccarEndpoints.DRIVERS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.drivers');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_DRIVERS, json);

      return json;
    },
    async [actions.CREATE_DRIVER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + traccarEndpoints.DRIVERS;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-save.drivers');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.UPDATE_DRIVER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + traccarEndpoints.DRIVERS + '/' + payload.id;
      const response = await requestHelper.apiPut(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-save.drivers');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.REMOVE_DRIVER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + traccarEndpoints.DRIVERS + '/' + payload.id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-save.drivers');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_GEO_FENCES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.GEO_FENCES;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.geo-fences');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_GEO_FENCES, json);

      return json;
    },
    async [actions.GET_USER_GEO_FENCES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_GEO_FENCES;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.geo-fences');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_USER_GEO_FENCES, json);

      return json;
    },
    async [actions.CREATE_USER_GEO_FENCE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_GEO_FENCES;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-save.geo-fence');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.UPDATE_USER_GEO_FENCE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_GEO_FENCES + '/' + payload.id;
      const response = await requestHelper.apiPut(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-save.geo-fence');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_USER_GEO_FENCE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_GEO_FENCES + '/' + payload.id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.geo-fence');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.CREATE_USER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.USERS;
      const response = await requestHelper.apiPost(endpoint, payload, null);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-create.user');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },

    async [actions.GET_COMPANIES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.COMPANIES;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.companies');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_COMPANIES, json);

      return json;
    },
    async [actions.GET_USER_COMPANY]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_COMPANY;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.companies');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      if (json?.id) {
        commit(mutations.SET_COMPANIES, [json]);
      }

      return json;
    },
    async [actions.CREATE_COMPANY]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.COMPANIES;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-create.company');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_COMPANY]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      delete payload.id;

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.COMPANIES + '/' + id;
      const response = await requestHelper.apiPatch(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.company');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_USER_COMPANY_DATA]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_COMPANY;
      const response = await requestHelper.apiPatch(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.company');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_COMPANIES, [json]);

      return json;
    },

    async [actions.GET_GROUPS]({commit}, params) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.GROUPS;
      const response = await requestHelper.apiGet(endpoint, null, params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.on-boarding-groups');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_GROUPS, json);

      return json;
    },
    async [actions.CREATE_GROUP]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.GROUPS;
      const response = await requestHelper.apiPost(endpoint, payload, null);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-create.group');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_GROUP]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.GROUPS + '/' + id;
      const response = await requestHelper.apiPut(endpoint, payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-edit.group');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_GROUP]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.GROUPS + '/' + id;
      const response = await requestHelper.apiDelete(endpoint);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.group');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },

    async [actions.CREATE_PERMISSION]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.PERMISSIONS;
      const response = await requestHelper.apiPost(endpoint, payload, null);

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      if (!response.ok) {
        throw new Error('error.failed-to-create.permission');
      }
    },

    async [actions.GET_DEVICES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.DEVICES;
      const response = await requestHelper.apiGet(endpoint);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.devices');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_DEVICES, json);

      return json;
    },
    async [actions.GET_ATTRIBUTES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.ATTRIBUTES;
      const response = await requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.attributes');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.CREATE_DEVICE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.DEVICES;
      const response = await requestHelper.apiPost(endpoint, payload, null);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-create.device');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_DEVICE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.DEVICES + '/' + id;
      const response = await requestHelper.apiPut(endpoint, payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-edit.device');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_DEVICE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.DEVICES + '/' + id;
      const response = await requestHelper.apiDelete(endpoint);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.device');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.SET_DEVICE_ACCUMULATORS]({commit}, data){
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const payload = {
        deviceId: data.deviceId
      };
      if (data.totalDistance !== null && data.totalDistance !== undefined) {
        payload.totalDistance = data.totalDistance;
      }
      if (data.minutes !== null && data.minutes !== undefined) {
        payload.hours = dataManipulator.convertMinutesToMilliseconds(data.minutes);
      }
      else if (data.hours !== null && data.hours !== undefined) {
        payload.hours = dataManipulator.convertHoursToMilliseconds(data.hours);
      }

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.DEVICES + '/' + payload.deviceId + '/accumulators';
      const response = await requestHelper.apiPut(endpoint, payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-edit.device-accumulators');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    [actions.UPDATE_POSITION_ATTRIBUTES]({commit, state}) {
      state.positions.forEach(position => {
        const device = entityDataManager.getById(state.devices, position.deviceId);
        if (!device) {
          return;
        }

        const ignition = entityDataManager.getPositionAttribute(position, 'ignition', false);

        commit(mutations.SET_DEVICE_IGNITION, {
          deviceId: device.id,
          ignition: ignition
        });

        let beaconUUID = entityDataManager.getPositionAttribute(position, 'beacon1Uuid') ??
            entityDataManager.getPositionAttribute(position, 'lastDriver');
        if (beaconUUID) {
          commit(mutations.SET_DEVICE_DRIVER, {
            deviceId: device.id,
            beaconUUID: beaconUUID,
            timestamp: entityDataManager.getTimeByPosition(position)
          });
        }

        // old logic
        /*if (device.status !== 'online') {
          commit(mutations.SET_DEVICE_DRIVER, {
            deviceId: device.id,
            beaconUUID: null
          });
        }
        else {
          let beaconUUID = entityDataManager.getPositionAttribute(position, 'beacon1Uuid');
          if (beaconUUID) {
            commit(mutations.SET_DEVICE_DRIVER, {
              deviceId: device.id,
              beaconUUID,
              timestamp: entityDataManager.getTimeByPosition(position)
            });
          }

          const beaconData = state.deviceDrivers.get(device.id);
          if (beaconData?.timestamp) {
            const diffInSeconds = moment.duration(moment().diff(beaconData?.timestamp)).asSeconds();

            // in case the time expired - remove the saved driver data
            if (diffInSeconds >= 90) {
              commit(mutations.SET_DEVICE_DRIVER, {
                deviceId: device.id,
                beaconUUID: null
              });
            }
          }
        }*/
      })
    },
    async [actions.GET_MAKES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAKES;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.makes');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_MAKES, json);

      return json;
    },
    async [actions.GET_MODELS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MODELS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.models');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_MODELS, json);

      return json;
    },

    async [actions.GET_VEHICLES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.vehicles');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_VEHICLES, json);

      return json;
    },
    async [actions.CREATE_VEHICLE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-create.vehicle');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_VEHICLE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      delete payload.id;

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + id;
      const response = await requestHelper.apiPatch(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.vehicle');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_VEHICLE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.vehicle');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_VEHICLE_ODOMETER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + id + '/odometer';
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'), payload);

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.SET_VEHICLE_ODOMETER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + id + '/odometer/today';
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_VEHICLE_LAST_POSITION]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload?.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + id + '/last-position';
      const response = await requestHelper.apiGetAnonymous(endpoint);

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.ENABLE_VEHICLE_IMMOBILIZER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      try {
        const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + payload?.id + '/immobilizer/enable';
        const response = await requestHelper.apiPost(endpoint, undefined, window.localStorage.getItem('_token'));

        if (!response.ok) {
          throw new Error();
        }
      }
      catch {
        throw new Error('error.failed-to-enable.vehicle-immobilizer');
      }
      finally {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
      }
    },
    async [actions.DISABLE_VEHICLE_IMMOBILIZER]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      try {
        const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES + '/' + payload?.id + '/immobilizer/disable';
        const response = await requestHelper.apiPost(endpoint, undefined, window.localStorage.getItem('_token'));

        if (!response.ok) {
          throw new Error();
        }
      }
      catch {
        throw new Error('error.failed-to-disable.vehicle-immobilizer');
      }
      finally {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
      }
    },
    async [actions.GET_VEHICLE_RECIPIENTS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLE_RECIPIENTS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.vehicle-recipients');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_VEHICLE_RECIPIENTS, json);

      return json;
    },
    async [actions.CREATE_VEHICLE_RECIPIENT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLE_RECIPIENTS;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-create.vehicle-recipient');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_VEHICLE_RECIPIENT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      delete payload.id;

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLE_RECIPIENTS + '/' + id;
      const response = await requestHelper.apiPatch(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.vehicle-recipient');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_VEHICLE_RECIPIENT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const id = payload.id || null;
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLE_RECIPIENTS + '/' + id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.vehicle-recipient');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_TASKS]({commit, state}, payload = {}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const saveInStore = payload.saveInStore ?? true;
      const filters = {
        status: payload.status ?? state.tasksFilterStatus,
        dateFrom: payload.dateFrom ?? state.tasksFilterDateFrom,
        dateTo: payload.dateTo ?? state.tasksFilterDateTo,
        driver: payload.driver ?? state.tasksFilterDriver,
        vehicle: payload.vehicle ?? state.tasksFilterVehicle,
        tags: payload.tags ?? state.tasksFilterTags,
      };
      const params = new URLSearchParams();
      if (filters.status) {
        params.append('status', filters.status);
      }

      if (filters.dateFrom) {
        params.append('dateFrom', dataManipulator.dateTimeToString(filters.dateFrom, true));
      }

      if (filters.dateTo) {
        params.append('dateTo', dataManipulator.dateTimeToString(filters.dateTo, true));
      }

      if (filters.driver?.id) {
        params.append('driverId', filters.driver.id);
      }

      if (filters.vehicle?.id) {
        params.append('vehicleId', filters.vehicle.id);
      }

      if (filters.tags?.length > 0) {
        filters.tags.forEach(tag => {
          const value = tag?.value ?? tag;
          if (value) {
            params.append('tags[]', value);
          }
        });
      }

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/user';
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.tasks');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      if (saveInStore) {
        commit(mutations.SET_TASKS, json);
      }

      return json;
    },
    async [actions.SYNC_OAZIS_DAILY]({commit}, payload = {}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.OAZIS_DAILY_SYNC;
      const requestPayload = payload.date ? {date: payload.date} : {};
      const response = await requestHelper.apiPost(endpoint, requestPayload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-sync.oazis');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_OAZIS_REQUESTS]({commit}, payload = {}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const params = new URLSearchParams();
      if (payload.date) {
        params.append('date', payload.date);
      }

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.OAZIS_REQUESTS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.oazis-requests');
      }

      const json = await response.json() || [];
      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.GET_TASK_VIEW_ONLY]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id + '/view-only';
      const response = await requestHelper.apiGetAnonymous(endpoint,
          {
            key: payload.key,
          });

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.tasks');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.CREATE_TASK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-create.tasks');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.UPDATE_TASK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id;
      const response = await requestHelper.apiPut(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.tasks');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_TASK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.tasks');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.SEND_TASK_LINK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id + '/send-link-sms';
      const response = await requestHelper.apiPost(endpoint, null, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: null});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.ACCEPT_TASK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id + '/accept-task';
      const response = await requestHelper.apiPostAnonymous(endpoint,
          null,
          {
            key: payload.key,
          });

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.VALIDATE_TASK]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id + '/validate';
      const response = await requestHelper.apiPost(endpoint, null, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK, json);

      return json;
    },
    async [actions.DETECT_TASK_VEHICLE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS + '/' + payload.id + '/detect-vehicle';
      const response = await requestHelper.apiPost(endpoint, null, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-detect.task-vehicle');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_TASK_SCHEDULES]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/user';
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.task-schedules');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULES, json);

      return json;
    },
    async [actions.GET_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/' + payload.id;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.task-schedules');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULE, json);

      return json;
    },
    async [actions.CREATE_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES;
      const response = await requestHelper.apiPost(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-create.task-schedule');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULE, json);

      return json;
    },
    async [actions.UPDATE_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/' + payload.id;
      const response = await requestHelper.apiPut(endpoint, payload, window.localStorage.getItem('_token'));

      const json = await response.json() || {};

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false, errors: json.errors || null});
        throw new Error('error.failed-to-edit.task-schedule');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULE, json);

      return json;
    },
    async [actions.DELETE_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/' + payload.id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-delete.task-schedule');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.REMOVE_TASK_SCHEDULE, payload);
    },
    async [actions.ACTIVATE_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/' + payload.id + '/activate';
      const response = await requestHelper.apiPost(endpoint, undefined, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-edit.task-schedule');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULE, json);

      return json;
    },
    async [actions.DEACTIVATE_TASK_SCHEDULE]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASK_SCHEDULES + '/' + payload.id + '/deactivate';
      const response = await requestHelper.apiPost(endpoint, undefined, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-edit.task-schedule');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TASK_SCHEDULE, json);

      return json;
    },
    async [actions.CHECK_VEHICLE_TAXES]({commit}, payload) {
      const id = payload?.id;
      if (!id) {
        return;
      }

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.CHECK_VEHICLE_TAXES + '/' + id;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.check-vehicle-taxes');
      }

      const json = await response.json() || {};

      commit(mutations.SET_VEHICLE, json);
      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.CHECK_VIOLATIONS]({commit, getters}) {
      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.CHECK_VIOLATIONS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        throw new Error('error.failed-to-get.check-violations');
      }

      const json = await response.json() || {};

      commit(mutations.SET_VIOLATIONS, json);

      if (getters.hasViolations) {
        const message = `<b>${i18n.global.t('settings.car-fleet.form.available-violations')}</b>`;
        notification.warning(message);
      }
    },
    async [actions.GET_MAINTENANCES_BY_USER]({commit}, payload = null) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const filter = new URLSearchParams();
      const payloadObj = payload ?? {};
      const appendFilterValue = (key, value) => {
        if (value === null || value === undefined || value === '') {
          return;
        }

        filter.append(key, value);
      };

      if (Array.isArray(payloadObj.deviceId)) {
        payloadObj.deviceId.forEach(value => appendFilterValue('deviceId[]', value));
      }
      else {
        appendFilterValue('deviceId', payloadObj.deviceId);
      }

      if (Array.isArray(payloadObj.status)) {
        payloadObj.status.forEach(value => appendFilterValue('status[]', value));
      }
      else {
        appendFilterValue('status', payloadObj.status);
      }

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES_USER;
      const params = filter.toString().length > 0 ? filter : {};
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || [];

      commit(mutations.SET_MAINTENANCES, json);
      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.GET_TRACCAR_MAINTENANCES_BY_USER]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES_TRACCAR_USER;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || [];

      commit(mutations.SET_TRACCAR_MAINTENANCES, json);
      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.EDIT_MAINTENANCE]({commit}, payload) {
      const id = payload?.id;
      if (!id) {
        return null;
      }

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES + '/' + id;
      const response = await requestHelper.apiPut(endpoint, payload, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.DELETE_MAINTENANCE]({commit}, payload) {
      const id = payload?.id;
      if (!id) {
        return null;
      }

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES + '/' + id;
      const response = await requestHelper.apiDelete(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return null;
    },
    async [actions.CREATE_MAINTENANCE_BY_DEVICE_ID]({commit}, payload) {
      const deviceId = payload?.deviceId;
      if (!deviceId) {
        return null;
      }

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES_DEVICE + '/' + deviceId;
      const response = await requestHelper.apiPost(
          endpoint,
          {
            name: payload?.name ?? null,
            description: payload?.description ?? null,
            odometerPeriod: payload?.odometerPeriod ?? null,
            odometerPeriodConfigured: payload?.odometerPeriodConfigured ?? null,
            targetOdometer: payload?.targetOdometer ?? null,
            targetDate: payload?.targetDate ?? null,
            targetDateConfigured: payload?.targetDateConfigured ?? null,
            amount: payload?.amount ?? null,
          },
          window.localStorage.getItem('_token'),
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.COMPLETE_MAINTENANCE]({commit}, payload) {
      const id = payload?.id;
      if (!id) {
        return null;
      }

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MAINTENANCES + '/' + id + '/complete';
      const response = await requestHelper.apiPost(
          endpoint,
          {
            name: payload?.name ?? null,
            description: payload?.description ?? null,
            amount: payload?.amount ?? null,
            reschedule: payload?.reschedule ?? false,
            odometerPeriod: payload?.odometerPeriod ?? null,
            odometerPeriodConfigured: payload?.odometerPeriodConfigured ?? null,
            targetOdometer: payload?.targetOdometer ?? null,
            targetDate: payload?.targetDate ?? null,
            targetDateConfigured: payload?.targetDateConfigured ?? null,
            dismissTypedData: payload?.dismissTypedData ?? false,
          },
          window.localStorage.getItem('_token'),
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = response.status === 204 ? null : (await response.json() || {});

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.SHOW_MAINTENANCE_NOTIFICATIONS]({dispatch, state}) {
      state.maintenances.forEach(maintenance => {
        let eventPayload = null;
        if (maintenance.status === MaintenanceStatus.Due) {
          eventPayload = {
            type: 'maintenance',
            deviceId: maintenance.deviceId,
            maintenanceName: maintenance.name,
          }
        }
        else if (maintenance.status === MaintenanceStatus.Overdue) {
          eventPayload = {
            type: 'alarm',
            attributes: {
              alarm: 'maintenance',
            },
            deviceId: maintenance.deviceId,
            maintenanceName: maintenance.name,
          }
        }

        if (eventPayload) {
          dispatch(actions.EVENT_RECEIVED, eventPayload);
        }
      });
    },

    async [actions.GET_TRIPS_DATA]({commit, state}, payload = null) {
      const hasPayloadFilters = payload !== null && payload !== undefined;
      if (!hasPayloadFilters) {
        commit(mutations.CLEAR_TRIPS_MAP_DATA);
      }
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.REPORTS_TRIPS;
      const filters = new URLSearchParams();
      filters.append('from', state.tripsFilterDateFrom);
      filters.append('to', state.tripsFilterDateTo);
      filters.append('addresses', state.tripsFilterIncludeAddresses ? 1 : 0);

      if (state.tripsFilterDevice?.deviceId) {
        filters.append('deviceId', state.tripsFilterDevice.deviceId);
      }

      if (state.tripsFilterDriver?.id) {
        filters.append('driverId', state.tripsFilterDriver.id);
      }

      Object.entries(payload ?? {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          filters.set(key, value);
        }
      });

      const response = await requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
          filters,
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.reports.failed-to-load-trips');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      if (!hasPayloadFilters) {
        commit(mutations.SET_TRIPS_DATA, json);
      }

      return json;
    },
    async [actions.EXPORT_TRIPS_DATA]({commit, state}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.REPORTS_TRIPS_EXPORT;

      const response = await requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
          (() => {
            const filters = new URLSearchParams();
            filters.append('from', state.tripsFilterDateFrom);
            filters.append('to', state.tripsFilterDateTo);

            if (state.tripsFilterDevice?.deviceId) {
              filters.append('deviceId', state.tripsFilterDevice.deviceId);
            }

            if (state.tripsFilterDriver?.id) {
              filters.append('driverId', state.tripsFilterDriver.id);
            }

            return filters;
          })(),
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.reports.failed-to-load-trips');
      }

      const filename = `${payload.licensePlate} ${i18n.global.t('navbar.reports.trips')}.xlsx`;

      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_TRIPS_RECORD_POSITIONS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.POSITIONS;
      const response = await requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
          payload,
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.reports.failed-to-load-trip-positions');
      }

      const json = await response.json() || [];
      const tripsRecordPositions = [json];

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_TRIPS_RECORD_POSITIONS, tripsRecordPositions);

      return tripsRecordPositions;
    },
    async [actions.ACCUMULATE_TRIPS_RECORD_POSITIONS]({commit}, payload) {
      const isPayloadObject = payload !== null && typeof payload === 'object' && !Array.isArray(payload);
      const requestsPayload = isPayloadObject ? (payload.payload ?? []) : payload;
      const shouldSaveInStore = isPayloadObject ? payload.skipStore !== true : true;

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.POSITIONS;
      const responses = await Promise.all((requestsPayload ?? []).map(requestQuery => requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
          requestQuery,
      )));

      if (responses.some(response => !response.ok)) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.reports.failed-to-load-trip-positions');
      }

      const allJsons = await Promise.all(responses.map(response => response.json()));
      const json = allJsons.reduce((acc, json) => [...acc, json], []);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      if (shouldSaveInStore) {
        commit(mutations.SET_TRIPS_RECORD_POSITIONS, json);
      }

      return json;
    },
    async [actions.GET_TRIPS_RECORD_POSITIONS_WITH_STOPS]({commit, dispatch}, payload = null) {
      const isPayloadObject = payload !== null && typeof payload === 'object' && !Array.isArray(payload);
      const requestsPayload = isPayloadObject ? (payload.payload ?? []) : payload;
      const shouldSaveInStore = isPayloadObject ? payload.skipStore !== true : true;

      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const tripsRecordPositions = await dispatch(actions.ACCUMULATE_TRIPS_RECORD_POSITIONS, {
        payload: requestsPayload ?? [],
        skipStore: !shouldSaveInStore,
      });

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return tripsRecordPositions;
    },
    async [actions.GET_TRACCAR_POSITIONS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_TRACCAR_URL + '/' + traccarEndpoints.POSITIONS;
      const response = await requestHelper.apiGet(endpoint, null, payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.position');
      }

      const json = await response.json() || {};

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    [actions.EVENT_RECEIVED]({commit, state, getters}, payload) {
      const eventInfo = entityDataManager.getEventInfo(payload.type, payload.attributes?.alarm);
      payload.isSeen = state.eventsPanelOpened;
      payload.severiy = eventInfo.severity;

      if (!payload.maintenanceName && payload.maintenanceId) {
        payload.maintenanceName = entityDataManager.getMaintenanceName(payload.maintenanceId, state.traccarMaintenances);
      }

      if (!state.eventsPanelOpened && (getters?.userCompany?.showDeviceNotifications ?? true)) {
        const vehicle = entityDataManager.getVehicleByDeviceId(state.vehicles, payload.deviceId);

        const geoFenceName = entityDataManager.getGeoFenceName(payload.geofenceId, getters.allGeoFences);
        const eventMessage = entityDataManager.getTranslatedMessageByEvent(payload.type, payload.attributes?.alarm, geoFenceName, payload.maintenanceName);

        let message = `<b>${vehicle.nickname}</b>`;
        if (payload.eventTime) {
          const formattedTime = dataManipulator.iso8601DateStringToLocalMoment(payload.eventTime)
              .format(i18n.global.t('settings.generic.date-time-format-moment-extended'));
          message += `</br>${formattedTime}`;
        }

        message += `</br>${eventMessage}`;

        if (payload.severiy === EventSeverity.warning) {
          if (process.env.VUE_APP_ALARM_NOTIFICATION_TIMEOUT_SECONDS > 0) {
            payload.alarmTimeout = setTimeout(() => payload.isSeen = true,
                process.env.VUE_APP_ALARM_NOTIFICATION_TIMEOUT_SECONDS * 1000);
          }

          setTimeout(() => notification.warning(message), 30);
        }
        else if (payload.severiy === EventSeverity.alarm) {
          if (process.env.VUE_APP_ALARM_NOTIFICATION_TIMEOUT_SECONDS > 0) {
            payload.alarmTimeout = setTimeout(() => payload.isSeen = true,
                process.env.VUE_APP_ALARM_NOTIFICATION_TIMEOUT_SECONDS * 1000);
          }

          setTimeout(() => notification.alarm(message), 30);
        }
        else {
          setTimeout(() => notification.event(message), 30);
        }
      }

      commit(mutations.APPEND_EVENT, payload);
    },
    async [actions.GET_EVENTS_RECORDS]({commit, state}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.REPORTS_EVENTS;

      const params = new URLSearchParams();
      params.append('from', state.eventsFilterDateFrom);
      params.append('to', state.eventsFilterDateTo);

      if (state.eventsFilterDeviceIds?.length > 0) {
        state.eventsFilterDeviceIds.forEach(item => params.append('deviceId[]', item.deviceId));
      }
      else {
        state.vehicles.forEach(vehicle => params.append('deviceId[]', vehicle.deviceId));
      }

      if (state.eventsFilterStatuses?.length > 0) {
        state.eventsFilterStatuses.forEach(status => params.append('status[]', status.key));
      }

      const response = await requestHelper.apiGet(
          endpoint,
          window.localStorage.getItem('_token'),
          params
      );

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.reports.failed-to-load-trips');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});
      commit(mutations.SET_EVENTS_RECORDS, json);

      return json;
    },
    async [actions.GET_CURRENT_USER_ASSIGNED_EVENTS]({dispatch, commit, state}) {

      const response = await dispatch(actions.GET_USER_ASSIGNED_EVENTS, {userId: state.session.id});
      commit(mutations.SET_CURRENT_USER_ASSIGNED_EVENTS, response);

      return response;
    },
    async [actions.GET_ASSIGNABLE_EVENTS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.ASSIGNABLE_EVENTS;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.on-boarding-assignable-events');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.GET_USER_ASSIGNED_EVENTS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_ASSIGNED_EVENTS + payload.userId;
      const response = await requestHelper.apiGet(endpoint, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.on-boarding-user-assigned-events');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.SET_USER_ASSIGNED_EVENTS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.USER_ASSIGNED_EVENTS + payload.userId;
      const response = await requestHelper.apiPost(endpoint, payload.assignedEvents, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get.on-boarding-user-assigned-events');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    },
    async [actions.CLAIM_INTEREST_IN_EVENTS]({commit}) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const endpoint = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.CLAIM_INTEREST_EVENTS;
      const response = await requestHelper.apiPost(endpoint, {}, window.localStorage.getItem('_token'));

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-claim-interest-in-events');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_ROAD_LIST_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      for (const deviceId of payload.deviceIds) {
        const endpoint = endpoints.ROAD_LIST_REPORT
            .replace('{userId}', payload.userId)
            .replace('{deviceId}', deviceId);

        const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoint;
        const response = await requestHelper.apiGet(url,
            window.localStorage.getItem('_token'),
            {
              from: dataManipulator.dateTimeToString(payload.dateFrom),
              to: dataManipulator.dateTimeToString(payload.dateTo),
            });

        if (!response.ok) {
          commit(mutations.SET_IS_LOADING, {isLoading: false});
          throw new Error('error.failed-to-get-road-list-report');
        }

        const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
        const blob = await response.blob();

        saveAs(blob, filename);
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_MONTHLY_VEHICLE_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {
        isLoading: true,
        message: `<div>${i18n.global.t('reports.page.might-take-time')}</div><div>${i18n.global.t('generic.please-wait')}</div>`
      });

      for (const deviceId of payload.deviceIds) {
        const endpoint = endpoints.MONTHLY_VEHICLE_REPORT
            .replace('{userId}', payload.userId)
            .replace('{deviceId}', deviceId);

        const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoint;
        const response = await requestHelper.apiGet(url,
            window.localStorage.getItem('_token'),
            {
              from: dataManipulator.dateTimeToString(payload.dateFrom),
              to: dataManipulator.dateTimeToString(payload.dateTo),
            });

        if (!response.ok) {
          commit(mutations.SET_IS_LOADING, {isLoading: false});
          throw new Error('error.failed-to-get-monthly-vehicle-report');
        }

        const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
        const blob = await response.blob();

        saveAs(blob, filename);
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_MONTHLY_COMPANY_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.MONTHLY_COMPANY_REPORT + '/' + payload.userId;
      const response = await requestHelper.apiGet(url,
          window.localStorage.getItem('_token'),
          {
            from: dataManipulator.dateTimeToString(payload.dateFrom),
            to: dataManipulator.dateTimeToString(payload.dateTo),
            email: payload.email
          });

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get-monthly-company-report');
      }

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_IMPORTANT_EVENTS_REPORTS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const params = new URLSearchParams();
      params.append('from', dataManipulator.dateTimeToString(payload.dateFrom));
      params.append('to', dataManipulator.dateTimeToString(payload.dateTo));
      payload.vehicleIds.forEach(item => params.append('vehicleId[]', item));

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.IMPORTANT_EVENTS_REPORT + '/' + payload.userId;
      const response = await requestHelper.apiGet(url, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get-important-events-report');
      }

      const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_ENGINE_HOURS_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const params = new URLSearchParams();
      params.append('from', dataManipulator.dateTimeToString(payload.dateFrom));
      params.append('to', dataManipulator.dateTimeToString(payload.dateTo));
      payload.deviceIds.forEach(item => params.append('deviceId[]', item));

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.ENGINE_HOURS_REPORT;
      const response = await requestHelper.apiGet(url, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_TASKS_REPORTS]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.TASKS_REPORT;
      const response = await requestHelper.apiGet(url, window.localStorage.getItem('_token'), payload);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.failed-to-get-tasks-report');
      }

      const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_VEHICLES_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const params = new URLSearchParams();
      params.append('userId', payload.userId);
      payload.vehicleIds.forEach(item => params.append('vehicleId[]', item));

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.VEHICLES_REPORT;
      const response = await requestHelper.apiGet(url, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_DRIVERS_REPORT]({commit}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const params = new URLSearchParams();
      params.append('from', dataManipulator.dateTimeToString(payload.dateFrom));
      params.append('to', dataManipulator.dateTimeToString(payload.dateTo));
      payload.deviceIds?.forEach(item => params.append('deviceId[]', item));
      if (payload.driverId)
        params.append('driverId', payload.driverId);

      const url = process.env.VUE_APP_BACKEND_ENDPOINT_URL + '/' + endpoints.DRIVERS_REPORT;
      const response = await requestHelper.apiGet(url, window.localStorage.getItem('_token'), params);

      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const filename = dataAggregator.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
      const blob = await response.blob();

      saveAs(blob, filename);

      commit(mutations.SET_IS_LOADING, {isLoading: false});
    },
    async [actions.GET_NAVIGATION_DIRECTIONS]({commit, getters}, payload) {
      commit(mutations.SET_IS_LOADING, {isLoading: true});

      const routeApiUrl = 'https://routes.googleapis.com/directions/v2:computeRoutes';
      const routesRequest = {
        'origin': { 'location': { 'latLng': payload.origin } },
        'destination': { 'location': { 'latLng': payload.destination } },
        'travelMode': 'DRIVE',
        'computeAlternativeRoutes': false,
        'routingPreference': 'TRAFFIC_UNAWARE',
        'routeModifiers': { 'avoidTolls': false, 'avoidHighways': false, 'avoidFerries': false },
        'units': 'METRIC'
      };

      const routeApiHeaders = new Headers();
      routeApiHeaders.append('Content-Type', 'application/json');
      routeApiHeaders.append('X-Goog-Api-Key', getters.googleRoutesApiKey);
      routeApiHeaders.append('X-Goog-FieldMask', 'routes.polyline.encodedPolyline');
      const response = await requestHelper.apiPostAnonymous(routeApiUrl, routesRequest, {}, routeApiHeaders);
      if (!response.ok) {
        commit(mutations.SET_IS_LOADING, {isLoading: false});
        throw new Error('error.generic');
      }

      const json = await response.json() || [];

      commit(mutations.SET_IS_LOADING, {isLoading: false});

      return json;
    }
  },
});

export default store;
